import { useEffect, useState } from "react"
import { ChevronDown, ChevronRight } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import { useEmbeddingModels } from "@/pages/knowledge-bases/presentation/hooks/use-embedding-models"
import type {
  CreateKnowledgeBaseInput,
  KnowledgeBase,
} from "@/pages/knowledge-bases/domain/entities/knowledge-base"
import { useUpdateKnowledgeBase } from "../hooks/use-update-knowledge-base"

type Props = {
  open: boolean
  kb: KnowledgeBase
  onClose: () => void
}

export const EditKbDialog = ({ open, kb, onClose }: Props) => {
  const { data: embeddingModels = [] } = useEmbeddingModels()
  const { mutate: update, isPending } = useUpdateKnowledgeBase(kb.id)

  const [name, setName] = useState(kb.name)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [chunkSize, setChunkSize] = useState(kb.chunkingConfig.chunkSize)
  const [chunkOverlap, setChunkOverlap] = useState(kb.chunkingConfig.chunkOverlap)
  const [embeddingModel, setEmbeddingModel] = useState(kb.chunkingConfig.embeddingModelCode)

  useEffect(() => {
    if (!open) return
    setName(kb.name)
    setChunkSize(kb.chunkingConfig.chunkSize)
    setChunkOverlap(kb.chunkingConfig.chunkOverlap)
    setEmbeddingModel(kb.chunkingConfig.embeddingModelCode)
    setAdvancedOpen(false)
  }, [
    open,
    kb.id,
    kb.name,
    kb.chunkingConfig.chunkSize,
    kb.chunkingConfig.chunkOverlap,
    kb.chunkingConfig.embeddingModelCode,
  ])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    const input: CreateKnowledgeBaseInput = {
      name: name.trim(),
      chunkingConfig: {
        chunkSize: chunkSize,
        chunkOverlap,
        embeddingModelCode: embeddingModel,
      },
    }
    update(input, { onSuccess: onClose })
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="grid-rows-[auto_minmax(0,1fr)_auto] max-h-[85vh] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Knowledge Base</DialogTitle>
        </DialogHeader>

        <form
          id="edit-kb-form"
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-col gap-4 overflow-y-auto"
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-kb-name">Name *</Label>
            <Input
              id="edit-kb-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={isPending}
            />
          </div>

          <div>
            <button
              type="button"
              className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
              onClick={() => setAdvancedOpen((v) => !v)}
            >
              {advancedOpen ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
              Advanced configuration
            </button>

            {advancedOpen && (
              <div className="mt-3 flex flex-col gap-4 rounded-lg border p-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="edit-kb-chunk-size">Chunk size</Label>
                  <Input
                    id="edit-kb-chunk-size"
                    type="number"
                    min={1}
                    step={1}
                    value={chunkSize}
                    onChange={(e) => setChunkSize(parseInt(e.target.value, 10) || 0)}
                    disabled={isPending}
                  />
                  <p className="text-xs text-muted-foreground">Detail chunk size in tokens, e.g. 128</p>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="edit-kb-chunk-overlap">Chunk overlap</Label>
                  <Input
                    id="edit-kb-chunk-overlap"
                    type="number"
                    min={0}
                    max={1}
                    step={0.01}
                    value={chunkOverlap}
                    onChange={(e) => setChunkOverlap(parseFloat(e.target.value))}
                    disabled={isPending}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="edit-kb-embedding-model">Embedding model</Label>
                  <select
                    id="edit-kb-embedding-model"
                    value={embeddingModel}
                    onChange={(e) => setEmbeddingModel(e.target.value)}
                    disabled={isPending || embeddingModels.length === 0}
                    className={cn(
                      "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none",
                      "focus:ring-1 focus:ring-ring",
                      "disabled:cursor-not-allowed disabled:opacity-50",
                    )}
                  >
                    {embeddingModels.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.display}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" form="edit-kb-form" disabled={isPending || !name.trim()}>
            {isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
